import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  NotificationService,
  SkeletonLoaderComponent,
  StenaAffirmOrDismissDialogComponent,
  StenaAffirmOrDismissDialogData,
  StenaButtonComponent,
  StenaInputComponent,
} from '@app-monorepo/shared/ui/stena-components';
import { PeriodsApiService } from '../../../services/periods-api.service';
import { ReportingApiService } from '../../../services/reporting-api.service';
import { Month } from '../../../models/period.model';
import {
  DailyFormConfig,
  ReportingCostCenter,
} from '../../../models/reporting.model';
import { ContextPickerComponent } from '../../../shared/components/context-picker/context-picker.component';

interface CellEdit {
  day: number;
  kpiBaseId: number;
  value: string | null;
}

@Component({
  standalone: true,
  selector: 'app-reporting-daily-page',
  templateUrl: './reporting-daily-page.component.html',
  styleUrl: './reporting-daily-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ContextPickerComponent,
    SkeletonLoaderComponent,
    StenaButtonComponent,
    StenaInputComponent,
  ],
})
export class ReportingDailyPageComponent implements OnInit {
  private readonly reportingApi = inject(ReportingApiService);
  private readonly periodsApi = inject(PeriodsApiService);
  private readonly notify = inject(NotificationService);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);

  readonly costCenters = signal<ReportingCostCenter[]>([]);
  readonly months = signal<Month[]>([]);
  readonly selectedCostCenterId = signal<number | null>(null);
  readonly selectedMonthId = signal<number | null>(null);

  readonly form = signal<DailyFormConfig | null>(null);
  readonly cells = signal<Map<string, string | null>>(new Map());
  readonly loading = signal(false);
  readonly saving = signal(false);

  readonly workingDaysForm = this.fb.group({
    workingDays: this.fb.control<number | null>(null),
  });

  readonly monthOptions = computed(() =>
    this.months().filter((m) => !m.isFiscalYearMarker)
  );

  readonly days = computed(() => {
    const n = this.form()?.daysInMonth ?? 0;
    return Array.from({ length: n }, (_, i) => i + 1);
  });

  // Column-total footer (mirrors the legacy GridView FooterRow behaviour —
  // sums every numeric value in a KPI column and shows the total at the bottom).
  readonly columnTotals = computed<Map<number, number>>(() => {
    const totals = new Map<number, number>();
    const f = this.form();
    if (!f) return totals;
    for (const col of f.columns) {
      let sum = 0;
      for (const day of this.days()) {
        const raw = this.getValue(day, col.kpiBaseId);
        const n = raw !== null && raw !== '' ? Number(raw) : NaN;
        if (!Number.isNaN(n)) sum += n;
      }
      totals.set(col.kpiBaseId, sum);
    }
    return totals;
  });

  constructor() {
    effect(() => {
      const cc = this.selectedCostCenterId();
      const month = this.selectedMonthId();
      if (cc === null || month === null) {
        this.form.set(null);
        this.cells.set(new Map());
        this.workingDaysForm.controls.workingDays.setValue(null, {
          emitEvent: false,
        });
        return;
      }
      this.reportingApi.daily(cc, month).subscribe({
        next: (form) => {
          this.form.set(form);
          this.workingDaysForm.controls.workingDays.setValue(form.workingDays, {
            emitEvent: false,
          });
          const map = new Map<string, string | null>();
          for (const v of form.values) {
            map.set(this.cellKey(v.day, v.kpiBaseId), v.value);
          }
          this.cells.set(map);
        },
        error: () => this.err('Failed to load daily form'),
      });
    });
  }

  ngOnInit(): void {
    this.loading.set(true);
    this.reportingApi.costCenters().subscribe({
      next: (rows) => {
        this.costCenters.set(rows);
        this.periodsApi.list().subscribe({
          next: (months) => {
            this.months.set(months);
            this.loading.set(false);
          },
          error: () => {
            this.err('Failed to load months');
            this.loading.set(false);
          },
        });
      },
      error: () => {
        this.err('Failed to load cost centers');
        this.loading.set(false);
      },
    });
  }

  cellKey(day: number, kpiBaseId: number): string {
    return `${day}:${kpiBaseId}`;
  }

  getValue(day: number, kpiBaseId: number): string | null {
    return this.cells().get(this.cellKey(day, kpiBaseId)) ?? null;
  }

  setValue(day: number, kpiBaseId: number, value: string | null): void {
    const next = new Map(this.cells());
    next.set(this.cellKey(day, kpiBaseId), value);
    this.cells.set(next);
  }

  onSave(): void {
    const form = this.form();
    if (!form) return;
    const cells: CellEdit[] = [];
    for (const [key, value] of this.cells()) {
      const [dayStr, kpiStr] = key.split(':');
      cells.push({
        day: Number(dayStr),
        kpiBaseId: Number(kpiStr),
        value,
      });
    }
    this.saving.set(true);
    this.reportingApi
      .saveDaily({
        costCenterId: form.costCenterId,
        monthId: form.monthId,
        cells,
      })
      .subscribe({
        next: (result) => {
          this.notify.success(`Saved ${result.savedCount} cells`);
          this.saving.set(false);
        },
        error: () => {
          this.err('Failed to save cells');
          this.saving.set(false);
        },
      });
  }

  async onSaveWorkingDays(): Promise<void> {
    const form = this.form();
    const days = this.workingDaysForm.controls.workingDays.value;
    if (!form || days === null) return;
    const data: StenaAffirmOrDismissDialogData = {
      title: 'Save working days',
      description: `Set working days for this month to ${days}? This affects KPI-per-day calculations.`,
      affirmButtonText: 'Save',
      dismissButtonText: 'Cancel',
    };
    const ref = this.dialog.open(StenaAffirmOrDismissDialogComponent, { data });
    const confirmed = await firstValueFrom(ref.afterClosed());
    if (!confirmed) return;
    this.saving.set(true);
    this.reportingApi
      .saveWorkingDays(form.costCenterId, form.monthId, days)
      .subscribe({
        next: () => {
          this.notify.success('Working days saved');
          this.saving.set(false);
        },
        error: () => {
          this.err('Failed to save working days');
          this.saving.set(false);
        },
      });
  }

  private err(message: string): void {
    this.notify.error(message);
  }
}
